import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import {
  Box,
  Typography,
  Avatar,
  Stack,
  CircularProgress,
  Tooltip,
  Button,
  IconButton,
  Card,
  Divider,
} from "@mui/material";
import {
  PhotoCamera,
  Person,
  Email,
  Key,
  ArrowBack,
  CalendarMonth,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import { fetchUserProfile } from "../../services/api";
import { AuthContext } from "../../context/AuthContext";

const formatDate = (dateString) => {
  if (!dateString) return "2025"; // Default joined year
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric" });
  } catch {
    return dateString;
  }
};

const Profile = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { user: authUser } = useContext(AuthContext);
  const userId = authUser?.userId;

  const [user, setUser] = useState({
    avatarUrl: null,
    name: "",
    email: "",
    password: "",
    memberSince: "2025",
  });
  const [fetching, setFetching] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    let isMounted = true;

    const loadUserProfile = async () => {
      if (!userId) {
        setFetching(false);
        return;
      }
      if (!fetching) setFetching(true);

      try {
        const data = await fetchUserProfile(userId);
        if (data && isMounted) {
          const memberDate = data.memberSinceDate || "2025";

          setUser({
            avatarUrl: data.avatarUrl || null,
            name: `${data.firstName} ${data.lastName}`,
            email: data.email,
            password: data.password,
            memberSince: formatDate(memberDate),
          });
        }
      } catch (err) {
        
      } finally {
        if (isMounted) setFetching(false);
      }
    };

    loadUserProfile();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  const userInitials = useMemo(() => {
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }, [user.name]);

  if (fetching) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        width: "100%",
        p: 0,
        background: isDarkMode
          ? theme.palette.background.default
          : theme.palette.grey[100],
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: { xs: 1.5, md: 2 },
          bgcolor: isDarkMode
            ? "rgba(25, 25, 25, 0.6)"
            : "rgba(255,255,255,0.6)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: isDarkMode ? "none" : theme.shadows[1],
        }}
      >
        <Typography variant="h6" fontWeight={800} color="text.primary">
          Profile
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/")}
          startIcon={<ArrowBack />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 0.5, sm: 1 },
            fontSize: { xs: "0.7rem", sm: "0.8rem" },
            background: isDarkMode
              ? "linear-gradient(90deg,#4f46e5,#7c3aed)"
              : "linear-gradient(90deg,#2563eb,#9333ea)",
            "&:hover": { transform: "translateY(-1px)" },
            minWidth: 100,
          }}
        >
          Go Back
        </Button>
      </Box>

      {/* Centered Card */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 8,
        }}
      >
        <Card
          sx={{
            maxWidth: 1000,
            width: "95%",
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            boxShadow: isDarkMode ? theme.shadows[10] : theme.shadows[5],
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${
              isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"
            }`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 4, md: 6 },
            }}
          >
            {/* Left Panel */}
            <Box
              sx={{
                flexBasis: { md: "35%" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                p: { xs: 0, md: 2 },
                borderRadius: 3,
                bgcolor: isDarkMode
                  ? theme.palette.action.hover
                  : theme.palette.grey[50],
              }}
            >
              <Box sx={{ position: "relative", mb: 2 }}>
                <Avatar
                  src={user.avatarUrl || undefined}
                  sx={{
                    mt: 3,
                    width: 120,
                    height: 120,
                    bgcolor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    fontSize: 50,
                    fontWeight: 700,
                    border: `4px solid ${theme.palette.primary.main}`,
                    boxShadow: theme.shadows[3],
                  }}
                >
                  {!user.avatarUrl && userInitials}
                </Avatar>
                <Tooltip title="Change profile picture" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    sx={{
                      position: "absolute",
                      bottom: 4,
                      right: 4,
                      bgcolor: theme.palette.background.paper,
                      border: `2px solid ${theme.palette.background.paper}`,
                      "&:hover": {
                        bgcolor: theme.palette.background.default,
                      },
                    }}
                  >
                    <PhotoCamera fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                {user.name}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                <CalendarMonth color="action" sx={{ fontSize: 16 }} />
                <Typography color="text.secondary" fontSize={14}>
                  Joined {user.memberSince}
                </Typography>
              </Stack>
            </Box>

            {/* Right Panel */}
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                borderBottom={1}
                pb={1}
                borderColor="divider"
              >
                Account Information
              </Typography>

              <Stack spacing={3}>
                {/* Full Name */}
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                    gutterBottom
                  >
                    Full Name
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Person color="primary" />
                    <Typography variant="body1" fontWeight={500}>
                      {user.name}
                    </Typography>
                  </Stack>
                  <Divider sx={{ mt: 1 }} />
                </Box>

                {/* Email */}
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                    gutterBottom
                  >
                    Email Address
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Email color="primary" />
                    <Typography variant="body1" fontWeight={500}>
                      {user.email}
                    </Typography>
                  </Stack>
                  <Divider sx={{ mt: 1 }} />
                </Box>

                {/* Password */}
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                    gutterBottom
                  >
                    Password
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Key color="primary" />
                      <Typography variant="body1" fontWeight={500}>
                        {showPassword ? user.password : "••••••••"}
                      </Typography>
                    </Stack>
                    <Tooltip
                      title={showPassword ? "Hide password" : "Show password"}
                      arrow
                    >
                      <IconButton
                        onClick={togglePasswordVisibility}
                        size="small"
                        color="secondary"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              </Stack>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default Profile;
